import { Boss } from '../entities/Boss';
import { Dot } from '../entities/Dot';
import { BossType, Bounds, Vector2 } from '../types';
import type { Weapon } from '../weapons/Weapon';
import { KineticBomb } from '../weapons/KineticBomb';
import { Chainsaw } from '../weapons/Chainsaw';
import { IceBomb } from '../weapons/IceBomb';
import { NuclearBomb } from '../weapons/NuclearBomb';
import { ElectricBomb } from '../weapons/ElectricBomb';
import { circleCollision } from '../utils/math';

/**
 * Configuration for boss spawns within a level
 */
export interface BossConfig {
  enabled: boolean;
  /** Boss types that can spawn (random pick each time) */
  types: BossType[];
  /** ms between boss spawns (after the previous boss dies) */
  spawnIntervalMs: number;
  /** Initial delay before the first boss spawns */
  initialDelayMs: number;
}

export const DEFAULT_BOSS_CONFIG: BossConfig = {
  enabled: false,
  types: [],
  spawnIntervalMs: 25000,
  initialDelayMs: 15000,
};

interface DamageableWeapon {
  isActive(): boolean;
  getPosition(): Vector2;
  getRadius(): number;
}

function asDamageable(weapon: Weapon): DamageableWeapon | null {
  const w = weapon as unknown as DamageableWeapon;
  if (typeof w.isActive === 'function' &&
      typeof w.getPosition === 'function' &&
      typeof w.getRadius === 'function') {
    return w;
  }
  return null;
}

/**
 * BossManager - Owns the active boss lifecycle, applies/removes buffs on
 * arena dots, handles boss damage from weapons, and tracks splitter-baby dots.
 */
export class BossManager {
  private activeBoss: Boss | null = null;
  private config: BossConfig = { ...DEFAULT_BOSS_CONFIG };
  private spawnTimer: number = 0;
  private hasSpawnedOnce: boolean = false;

  /** Baby dots spawned by Splitter King. Tracked here independently of patterns. */
  private babyDots: Dot[] = [];

  configure(config: Partial<BossConfig>): void {
    this.config = { ...this.config, ...config };
  }

  isEnabled(): boolean {
    return this.config.enabled && this.config.types.length > 0;
  }

  reset(): void {
    this.activeBoss = null;
    this.spawnTimer = 0;
    this.hasSpawnedOnce = false;
    this.babyDots = [];
  }

  getActiveBoss(): Boss | null {
    return this.activeBoss;
  }

  /**
   * Baby dots spawned by Splitter King deaths.
   * Game.ts should merge these into the allDots array passed to weapons / collisions.
   */
  getBabyDots(): Dot[] {
    return this.babyDots;
  }

  private spawnRandomBoss(bounds: Bounds): void {
    if (this.config.types.length === 0) return;
    const type = this.config.types[Math.floor(Math.random() * this.config.types.length)];
    const margin = 80;
    const x = margin + Math.random() * (bounds.width - margin * 2);
    const y = margin + Math.random() * (bounds.height - margin * 2);
    this.activeBoss = new Boss(type, x, y);
  }

  /**
   * Update boss logic, buffs, splitting, and damage from weapons.
   */
  update(
    dt: number,
    bounds: Bounds,
    playerPosition: Vector2,
    allDots: Dot[],
    activeWeapons: Weapon[]
  ): void {
    if (!this.isEnabled()) {
      // Still update any leftover babies so they can expire
      this.updateBabies(dt, bounds, playerPosition);
      return;
    }

    if (!this.activeBoss) {
      this.spawnTimer += dt * 1000;
      const requiredDelay = this.hasSpawnedOnce
        ? this.config.spawnIntervalMs
        : this.config.initialDelayMs;
      if (this.spawnTimer >= requiredDelay) {
        this.spawnTimer = 0;
        this.hasSpawnedOnce = true;
        this.spawnRandomBoss(bounds);
      }
    } else {
      this.activeBoss.update(dt, bounds, playerPosition);

      const effect = this.activeBoss.getEffect();
      // Apply buff to all dots (including babies currently alive)
      for (const dot of allDots) {
        if (!dot.isDead()) {
          dot.addEffect(effect);
        }
      }
      for (const baby of this.babyDots) {
        if (!baby.isDead()) {
          baby.addEffect(effect);
        }
      }

      // Detect dots that died while splitting -> queue baby spawns
      for (const dot of allDots) {
        if (dot.shouldSplitOnDeath()) {
          const pos = dot.getPosition();
          const vx = dot.velocity?.x ?? 0;
          const vy = dot.velocity?.y ?? 0;
          this.spawnBabies(pos.x, pos.y, vx, vy);
        }
      }

      this.applyWeaponDamage(activeWeapons);

      if (this.activeBoss.isDead()) {
        // Remove buff from all dots when boss dies
        for (const dot of allDots) {
          dot.removeEffect(this.activeBoss.getEffect());
        }
        for (const baby of this.babyDots) {
          baby.removeEffect(this.activeBoss.getEffect());
        }
        this.activeBoss = null;
        this.spawnTimer = 0;
      }
    }

    this.updateBabies(dt, bounds, playerPosition);
  }

  private spawnBabies(x: number, y: number, vx: number, vy: number): void {
    for (let i = 0; i < 2; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 70 + Math.random() * 50;
      const baby = new Dot(x, y, null);
      baby.makeBaby();
      baby.skipSpawnAnimation();
      baby.velocity = {
        x: Math.cos(angle) * speed + vx * 0.3,
        y: Math.sin(angle) * speed + vy * 0.3,
      };
      this.babyDots.push(baby);
    }
  }

  private updateBabies(dt: number, bounds: Bounds, playerPosition: Vector2): void {
    for (let i = this.babyDots.length - 1; i >= 0; i--) {
      const baby = this.babyDots[i];
      baby.update(dt, bounds, playerPosition);

      // Cull babies that have flown off-screen
      const pos = baby.getPosition();
      if (pos.x < -50 || pos.x > bounds.width + 50 ||
          pos.y < -50 || pos.y > bounds.height + 50) {
        this.babyDots.splice(i, 1);
        continue;
      }

      if (baby.isDead()) {
        this.babyDots.splice(i, 1);
      }
    }
  }

  private applyWeaponDamage(activeWeapons: Weapon[]): void {
    if (!this.activeBoss) return;

    for (const weapon of activeWeapons) {
      if (weapon instanceof KineticBomb) {
        const kb = weapon as unknown as {
          explosionCenter: Vector2;
          explosionRadius: number;
        };
        if (kb.explosionRadius > 0) {
          this.activeBoss.tryDamage(kb.explosionCenter, kb.explosionRadius);
        }
        continue;
      }

      if (weapon instanceof Chainsaw) {
        const playerPos = (weapon as unknown as { playerPosition: Vector2 }).playerPosition;
        const radius = weapon.getChainsawRadius();
        if (playerPos) {
          this.activeBoss.tryDamage(playerPos, radius);
        }
        continue;
      }

      if (weapon instanceof IceBomb) {
        continue;
      }

      // NuclearBomb: damage from explosion area when exploding
      if (weapon instanceof NuclearBomb) {
        const nb = weapon as unknown as {
          explosionRadius: number;
          getPosition: () => Vector2;
          isActive: () => boolean;
        };
        if (nb.isActive() && nb.explosionRadius > 0) {
          this.activeBoss.tryDamage(nb.getPosition(), nb.explosionRadius);
        }
        continue;
      }

      // ElectricBomb: damage from explosion area when active
      if (weapon instanceof ElectricBomb) {
        const eb = weapon as unknown as {
          getRadius: () => number;
          getPosition: () => Vector2;
          isActive: () => boolean;
        };
        if (eb.isActive()) {
          this.activeBoss.tryDamage(eb.getPosition(), eb.getRadius());
        }
        continue;
      }

      const damageable = asDamageable(weapon);
      if (damageable && damageable.isActive()) {
        this.activeBoss.tryDamage(damageable.getPosition(), damageable.getRadius());
      }
    }
  }

  /**
   * Check boss-player collision (boss is lethal on contact)
   */
  checkPlayerCollision(playerPos: Vector2, playerRadius: number): boolean {
    if (!this.activeBoss) return false;
    if (!this.activeBoss.isLethal()) return false;
    return circleCollision(
      this.activeBoss.position,
      this.activeBoss.radius,
      playerPos,
      playerRadius
    );
  }

  renderBosses(renderer: import('../renderer/Renderer').Renderer): void {
    for (const baby of this.babyDots) {
      baby.render(renderer);
    }
    if (this.activeBoss) {
      this.activeBoss.render(renderer);
    }
  }
}
